import { Link } from "wouter";
import { useEffect } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  LoadingOverlay,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useContentPlansStore } from "./contentPlansStore";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { useDisclosure } from "@mantine/hooks";
import CreateContentPlanModal from "./CreateContentPlanModal";
import { useBrandContext } from "../Brands/brandContext";
import ClusterCard from "./ClusterCard";
import ClusterQuota from "./ClusterQuota";
import { clusterFromSummary } from "./clusterDisplay";

export default function ContentPlansList({ embedded = false }) {
  const { contentPlans, isLoading, fetchContentPlans, deleteContentPlan } =
    useContentPlansStore();
  const { selectedBrand } = useBrandContext();
  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    fetchContentPlans(selectedBrand?.id);
  }, [fetchContentPlans, selectedBrand?.id]);

  const generating = (contentPlans || []).filter(
    (p) => p.status === "generating"
  );
  const failed = (contentPlans || []).filter(
    (p) => p.status === "draft" && !(p.clusters || []).length
  );

  useEffect(() => {
    if (generating.length === 0) return undefined;
    const id = setInterval(() => {
      fetchContentPlans(selectedBrand?.id);
    }, 5000);
    return () => clearInterval(id);
  }, [generating.length, fetchContentPlans, selectedBrand?.id]);

  const plans = contentPlans || [];
  const showPlanName = plans.length > 1;
  const clusters = plans.flatMap((plan) =>
    (plan.clusters || []).map((c) => clusterFromSummary(c, plan))
  );

  const Wrapper = embedded ? "div" : Container;
  const wrapperProps = embedded ? {} : { size: "lg", py: "xl" };

  return (
    <Wrapper {...wrapperProps}>
      <Group justify="space-between" mb="xl" mt={embedded ? "md" : undefined} align="flex-start">
        <Stack gap={4}>
          {!embedded && <Title order={1}>Clusters</Title>}
          <ClusterQuota />
        </Stack>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>
          Propose clusters
        </Button>
      </Group>

      <LoadingOverlay visible={isLoading && plans.length === 0} />

      {generating.length > 0 && (
        <Alert color="blue" variant="light" mb="md" icon={<Loader size={16} />}>
          Proposing clusters
          {generating.length === 1 ? ` for ${generating[0].name}` : ""}…
        </Alert>
      )}

      {failed.map((plan) => (
        <Alert
          key={plan.id}
          color="red"
          variant="light"
          mb="md"
          title="Could not propose clusters"
        >
          <Group justify="space-between">
            <Text size="sm">{plan.name}</Text>
            <Group gap="xs">
              <Button size="xs" variant="light" component={Link} href={`/plans/${plan.id}`}>
                Open
              </Button>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() =>
                  modals.openConfirmModal({
                    title: "Delete this proposal",
                    children: (
                      <Text size="sm">
                        Delete "{plan.name}"? You can propose clusters again.
                      </Text>
                    ),
                    labels: { confirm: "Delete", cancel: "Cancel" },
                    confirmProps: { color: "red" },
                    onConfirm: () => deleteContentPlan(plan.id),
                  })
                }
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Alert>
      ))}

      {clusters.length > 0 ? (
        <Stack gap="md">
          {clusters.map((cluster) => (
            <ClusterCard
              key={`${cluster.planId}-${cluster.id}`}
              cluster={cluster}
              planName={showPlanName ? cluster.planName : null}
            />
          ))}
        </Stack>
      ) : (
        !isLoading &&
        generating.length === 0 &&
        failed.length === 0 && (
          <Center style={{ height: "40vh" }}>
            <Card padding="xl" radius="md" withBorder style={{ maxWidth: 420 }}>
              <Stack align="center" gap="md">
                <Text ta="center" c="dimmed">
                  No clusters yet. Propose a set of topics to research and write.
                </Text>
                <Button leftSection={<IconPlus size={16} />} onClick={open}>
                  Propose clusters
                </Button>
              </Stack>
            </Card>
          </Center>
        )
      )}

      <CreateContentPlanModal
        opened={opened}
        onClose={close}
        brandId={selectedBrand?.id}
        gscLinked={!!selectedBrand?.gscSiteUrl}
      />
    </Wrapper>
  );
}
